import { useEffect, useState } from "react";
import { api } from "@/lib/kfmApi";
import {
  Loader2, Users, Activity, TrendingUp, UserCheck, Repeat, DollarSign,
  Sparkles, AlertTriangle, RefreshCw, Link2, MousePointerClick, Headphones, Trophy,
} from "lucide-react";

const REASON_LABEL = {
  study_start: "New study",
  go_deeper: "Go Deeper chat",
  explain: "Simple explanation",
  cross_references: "Cross-references",
  prayer: "Prayer",
  study_to_sermon: "Study → Sermon",
  prayer_to_study: "Prayer → Study",
  admin_topup: "Admin top-up",
};
const reasonLabel = (r) => REASON_LABEL[r] || (r || "Activity").replace(/_/g, " ");
const fmt = (d) => { try { return new Date(d).toLocaleDateString(undefined, { month: "short", day: "numeric" }); } catch { return "—"; } };
const featureLabel = (f) =>
  ({ go_deeper: "Go Deeper", study_to_sermon: "Study → Sermon", prayer_to_study: "Prayer → Study", credits: "Out of credits" }[f] ||
    (f || "Unknown").replace(/_/g, " "));

function Metric({ icon: Icon, label, value, sub, testid, tone = "teal" }) {
  const tones = {
    teal: "text-[#00E5FF]",
    amber: "text-amber-400",
    rose: "text-rose-400",
    green: "text-emerald-400",
  };
  return (
    <div data-testid={testid} className="bg-[#0E131F] rounded-2xl border border-[#1E273D] p-5">
      <div className="flex items-center gap-2 text-xs uppercase tracking-widest text-slate-500">
        <Icon className={`w-4 h-4 ${tones[tone]}`} strokeWidth={1.5} />
        {label}
      </div>
      <div className="text-3xl text-white mt-2">{value}</div>
      {sub && <div className="text-xs text-slate-500 mt-1">{sub}</div>}
    </div>
  );
}

export default function GrowthDashboard() {
  const [data, setData] = useState(null);
  const [links, setLinks] = useState(null);
  const [linkFilter, setLinkFilter] = useState("");
  const [tts, setTts] = useState(null);
  const [savingTts, setSavingTts] = useState(false);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(false);

  const load = async () => {
    setLoading(true);
    setErr(false);
    try {
      const [{ data }, linksRes, ttsRes] = await Promise.all([
        api.get("/admin/growth"),
        api.get("/admin/links").catch(() => ({ data: null })),
        api.get("/admin/tts-usage").catch(() => ({ data: null })),
      ]);
      setData(data);
      setLinks(linksRes.data);
      setTts(ttsRes.data);
    } catch {
      setErr(true);
    }
    setLoading(false);
  };

  const toggleTts = async () => {
    if (!tts) return;
    setSavingTts(true);
    try {
      const { data } = await api.put("/admin/tts-config", { prewarm_enabled: !tts.prewarm_enabled });
      setTts((prev) => ({ ...prev, prewarm_enabled: data.prewarm_enabled }));
    } catch { /* ignore */ }
    setSavingTts(false);
  };

  const [savingPremium, setSavingPremium] = useState(false);
  const togglePremium = async () => {
    if (!tts?.premium_voice) return;
    setSavingPremium(true);
    try {
      const { data } = await api.put("/admin/premium-voice-config", { enabled: !tts.premium_voice.enabled });
      setTts((prev) => ({ ...prev, premium_voice: { ...prev.premium_voice, enabled: data.enabled } }));
    } catch { /* ignore */ }
    setSavingPremium(false);
  };

  useEffect(() => {
    load();
  }, []);

  if (loading)
    return (
      <div className="flex justify-center py-12">
        <Loader2 className="w-5 h-5 text-[#00E5FF] animate-spin" strokeWidth={1.5} />
      </div>
    );
  if (err)
    return (
      <div className="bg-[#0E131F] rounded-2xl border border-[#1E273D] p-6 text-center space-y-3">
        <p className="text-sm text-slate-400">{"Couldn't load growth data."}</p>
        <button data-testid="growth-retry" onClick={load} className="text-sm text-[#00E5FF] hover:underline">
          Try again
        </button>
      </div>
    );

  const money = (n) => `$${Number(n || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

  return (
    <div data-testid="admin-growth" className="space-y-6">
      <div className="flex items-center justify-between">
        <p className="text-sm text-slate-400">Real numbers only — admin/owner accounts are excluded.</p>
        <button
          data-testid="growth-refresh"
          onClick={load}
          className="flex items-center gap-1.5 text-xs text-slate-500 hover:text-[#00E5FF] transition-colors"
        >
          <RefreshCw className="w-3.5 h-3.5" strokeWidth={1.5} /> Refresh
        </button>
      </div>

      {/* Active users */}
      <div>
        <h3 className="text-xs font-semibold uppercase tracking-widest text-[#00E5FF] mb-3">Active members</h3>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          <Metric testid="growth-dau" icon={Activity} label="DAU" value={data.dau} sub="active today" />
          <Metric testid="growth-wau" icon={Activity} label="WAU" value={data.wau} sub="last 7 days" />
          <Metric testid="growth-mau" icon={Activity} label="MAU" value={data.mau} sub="last 30 days" />
          <Metric testid="growth-stickiness" icon={Repeat} label="Stickiness" value={`${data.dau_wau_ratio}%`} sub="DAU / WAU" />
        </div>
      </div>

      {/* Funnel */}
      <div>
        <h3 className="text-xs font-semibold uppercase tracking-widest text-[#00E5FF] mb-3">Signup → paid funnel</h3>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          <Metric testid="growth-total-users" icon={Users} label="Members" value={data.total_users} sub="total signups" />
          <Metric testid="growth-activation" icon={UserCheck} label="Activation" value={`${data.activation_rate}%`} sub={`${data.activated} did a study`} tone="green" />
          <Metric testid="growth-conversion" icon={TrendingUp} label="Conversion" value={`${data.conversion_rate}%`} sub={`${data.paid_count} paying`} tone="green" />
          <Metric testid="growth-churn" icon={AlertTriangle} label="Churn" value={`${data.churn_rate}%`} sub={`${data.churned} lapsed · ${data.canceling} canceling`} tone="rose" />
        </div>
      </div>

      {/* Revenue */}
      <div>
        <h3 className="text-xs font-semibold uppercase tracking-widest text-[#00E5FF] mb-3">Revenue</h3>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          <Metric testid="growth-revenue" icon={DollarSign} label="Revenue" value={money(data.revenue)} sub="all-time paid" />
          <Metric testid="growth-paying" icon={Users} label="Customers" value={data.paying_customers} sub="ever paid" />
          <Metric testid="growth-ltv" icon={DollarSign} label="LTV" value={money(data.ltv)} sub="per customer" />
          <Metric testid="growth-beta" icon={Sparkles} label="Beta" value={data.beta_count} sub="comped testers" />
        </div>
      </div>

      {/* Feature adoption */}
      <div data-testid="growth-adoption" className="bg-[#0E131F] rounded-2xl border border-[#1E273D] p-5">
        <h3 className="text-xs font-semibold uppercase tracking-widest text-[#00E5FF] mb-3">Feature adoption</h3>
        {data.adoption?.length ? (
          <div className="space-y-2">
            {data.adoption.map((a) => {
              const max = data.adoption[0]?.events || 1;
              return (
                <div key={a.reason} className="flex items-center gap-3">
                  <div className="w-40 shrink-0 text-sm text-slate-300">{reasonLabel(a.reason)}</div>
                  <div className="flex-1 h-2.5 rounded-full bg-[#141B29] overflow-hidden">
                    <div className="h-full bg-[#00E5FF] rounded-full" style={{ width: `${Math.round((a.events / max) * 100)}%` }} />
                  </div>
                  <div className="w-28 shrink-0 text-right text-xs text-slate-400">
                    {a.events} uses · {a.users} {a.users === 1 ? "member" : "members"}
                  </div>
                </div>
              );
            })}
          </div>
        ) : (
          <p className="text-sm text-slate-500">No activity recorded yet.</p>
        )}
      </div>

      {/* Upgrade friction */}
      <div className="bg-[#0E131F] rounded-2xl border border-[#1E273D] p-5">
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-xs font-semibold uppercase tracking-widest text-[#00E5FF]">Upgrade friction (limit hits)</h3>
          <span className="text-xs text-slate-500">{data.limit_hits_month} this month · {data.limit_hits_total} all-time</span>
        </div>
        {data.limit_by_feature?.length ? (
          <div className="flex flex-wrap gap-2" data-testid="growth-limits">
            {data.limit_by_feature.map((l) => (
              <span key={l.feature} className="inline-flex items-center gap-1.5 rounded-full bg-amber-500/10 border border-amber-500/30 text-amber-300 text-xs px-3 py-1.5">
                {featureLabel(l.feature)} <span className="font-semibold">{l.count}</span>
              </span>
            ))}
          </div>
        ) : (
          <p className="text-sm text-slate-500">No one has hit a limit yet — good sign, or turn features on.</p>
        )}
      </div>

      {/* Signups by source (UTM attribution) */}
      <div data-testid="growth-sources" className="bg-[#0E131F] rounded-2xl border border-[#1E273D] p-5">
        <h3 className="text-xs font-semibold uppercase tracking-widest text-[#00E5FF] mb-3 flex items-center gap-2">
          <UserCheck className="w-4 h-4" strokeWidth={1.5} /> Signups by source
        </h3>
        {data.signups_by_source?.length ? (
          <div className="space-y-2">
            {data.signups_by_source.map((s) => {
              const max = data.signups_by_source[0]?.count || 1;
              return (
                <div key={s.source} className="flex items-center gap-3" data-testid={`source-row-${s.source}`}>
                  <div className="w-32 shrink-0 text-sm text-slate-300 capitalize">{s.source}</div>
                  <div className="flex-1 h-2.5 rounded-full bg-[#141B29] overflow-hidden">
                    <div className="h-full bg-[#00E5FF] rounded-full" style={{ width: `${Math.round((s.count / max) * 100)}%` }} />
                  </div>
                  <div className="w-24 shrink-0 text-right text-xs text-slate-400">{s.count} {s.count === 1 ? "signup" : "signups"}</div>
                </div>
              );
            })}
          </div>
        ) : (
          <p className="text-sm text-slate-500">No signups attributed yet.</p>
        )}
        {data.signups_by_campaign?.length > 0 && (
          <div className="mt-4 pt-3 border-t border-[#1E273D]">
            <div className="text-[11px] uppercase tracking-widest text-slate-500 mb-2">Top campaigns</div>
            <div className="flex flex-wrap gap-2">
              {data.signups_by_campaign.slice(0, 12).map((c) => (
                <span key={c.campaign} className="inline-flex items-center gap-1.5 rounded-full bg-[#10222A] text-[#00E5FF] text-xs px-3 py-1.5">
                  {c.campaign} <span className="font-semibold">{c.count}</span>
                </span>
              ))}
            </div>
          </div>
        )}
        {data.conversion_by_source?.length > 0 && (
          <div className="mt-4 pt-3 border-t border-[#1E273D]" data-testid="growth-conversion">
            <div className="text-[11px] uppercase tracking-widest text-slate-500 mb-2">Conversion (clicks → signups)</div>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-left text-[10px] uppercase tracking-widest text-slate-500">
                    <th className="py-1 pr-3 font-medium">Source</th>
                    <th className="py-1 px-3 font-medium text-center">Clicks</th>
                    <th className="py-1 px-3 font-medium text-center">Signups</th>
                    <th className="py-1 pl-3 font-medium text-right">Rate</th>
                  </tr>
                </thead>
                <tbody>
                  {data.conversion_by_source.map((c) => (
                    <tr key={c.source} data-testid={`conversion-row-${c.source}`} className="border-t border-[#1E273D]">
                      <td className="py-1.5 pr-3 capitalize text-slate-300">{c.source}</td>
                      <td className="py-1.5 px-3 text-center text-slate-400">{c.clicks}</td>
                      <td className="py-1.5 px-3 text-center text-slate-400">{c.signups}</td>
                      <td className="py-1.5 pl-3 text-right font-medium text-[#00E5FF]">{c.rate === null ? "—" : `${c.rate}%`}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>

      {/* Referral leaderboard — most active personal sharers, ranked by total clicks */}
      {links?.leaderboard?.length > 0 && (
        <div data-testid="growth-leaderboard" className="bg-[#0E131F] rounded-2xl border border-[#1E273D] p-5">
          <h3 className="text-xs font-semibold uppercase tracking-widest text-[#00E5FF] flex items-center gap-2 mb-3">
            <Trophy className="w-4 h-4" strokeWidth={1.5} /> Referral leaderboard
          </h3>
          <div className="space-y-2">
            {links.leaderboard.slice(0, 10).map((p) => (
              <div key={p.email} data-testid={`leaderboard-row-${p.rank}`}
                className="flex items-center justify-between rounded-xl px-3 py-2 bg-[#0B0F16]">
                <div className="flex items-center gap-3">
                  <span className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-semibold ${
                    p.rank === 1 ? "bg-amber-500/15 text-amber-300"
                    : p.rank === 2 ? "bg-[#1E273D] text-slate-400"
                    : p.rank === 3 ? "bg-orange-500/15 text-orange-300"
                    : "bg-[#0E131F] text-slate-500 border border-[#29344D]"}`}>
                    {p.rank}
                  </span>
                  <div>
                    <div className="text-sm font-medium text-slate-200">{p.name}</div>
                    <div className="text-xs text-slate-500">{p.email}</div>
                  </div>
                </div>
                <div className="text-right">
                  <div className="text-sm font-semibold text-slate-200">{p.clicks} clicks</div>
                  <div className="text-xs text-slate-500">{p.links} link{p.links === 1 ? "" : "s"} shared</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Link performance (short-link clicks) */}
      <div data-testid="growth-links" className="bg-[#0E131F] rounded-2xl border border-[#1E273D] p-5">
        <div className="flex items-center justify-between mb-3 gap-3 flex-wrap">
          <h3 className="text-xs font-semibold uppercase tracking-widest text-[#00E5FF] flex items-center gap-2">
            <Link2 className="w-4 h-4" strokeWidth={1.5} /> Link performance
          </h3>
          <span data-testid="growth-links-summary" className="text-xs text-slate-500 flex items-center gap-1">
            <MousePointerClick className="w-3.5 h-3.5" /> {links?.total_clicks ?? 0} total clicks · {links?.count ?? 0} links
            {!links?.shortio_configured && " · Short.io not connected (using internal shortener)"}
          </span>
        </div>
        {links?.links?.length ? (
          <>
            <input
              data-testid="growth-links-filter"
              value={linkFilter}
              onChange={(e) => setLinkFilter(e.target.value)}
              placeholder="Filter by name, email, campaign or source…"
              className="w-full mb-3 h-9 rounded-lg border border-[#29344D] px-3 text-sm text-slate-300 focus:outline-none focus:border-[#00E5FF]/50"
            />
            {(() => {
              const q = linkFilter.trim().toLowerCase();
              const filtered = q
                ? links.links.filter((l) => [l.shared_by, l.campaign, l.source, l.ref]
                    .some((v) => (v || "").toLowerCase().includes(q)))
                : links.links;
              return filtered.length ? (
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="text-left text-[10px] uppercase tracking-widest text-slate-500 border-b border-[#1E273D]">
                        <th className="py-2 pr-3 font-medium">What</th>
                        <th className="py-2 px-3 font-medium">Source</th>
                        <th className="py-2 px-3 font-medium">Shared by</th>
                        <th className="py-2 px-3 font-medium text-center">Clicks</th>
                        <th className="py-2 px-3 font-medium">Top country</th>
                        <th className="py-2 pl-3 font-medium">Last click</th>
                      </tr>
                    </thead>
                    <tbody>
                      {filtered.slice(0, 40).map((l) => (
                        <tr key={l.code} data-testid={`link-row-${l.code}`} className="border-b border-[#1E273D] last:border-0">
                          <td className="py-2 pr-3">
                            <div className="flex items-center gap-2">
                              {l.kind === "nugget" && <span className="text-[10px] uppercase bg-[#10222A] text-[#00E5FF] px-1.5 py-0.5 rounded">Nugget</span>}
                              <span className="text-slate-300">{l.ref || l.campaign || l.code}</span>
                              {l.provider === "shortio" && <span data-testid={`link-row-${l.code}-shortio`} className="text-[9px] uppercase bg-[#00E5FF]/10 text-[#00E5FF] px-1.5 py-0.5 rounded">Short.io</span>}
                            </div>
                          </td>
                          <td className="py-2 px-3 text-slate-400 capitalize">{l.source}</td>
                          <td className="py-2 px-3 text-slate-400">{l.shared_by || "—"}</td>
                          <td className="py-2 px-3 text-center font-medium text-slate-200">{l.clicks}</td>
                          <td className="py-2 px-3 text-slate-400">{l.top_country || "—"}</td>
                          <td className="py-2 pl-3 text-slate-500 text-xs">{l.last_click_at ? fmt(l.last_click_at) : "—"}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <p data-testid="growth-links-no-match" className="text-sm text-slate-500">No links match "{linkFilter}".</p>
              );
            })()}
          </>
        ) : (
          <p className="text-sm text-slate-500">No tracked links yet — approve a nugget or post and its link will appear here once it gets clicks.</p>
        )}
      </div>

      {/* Audio narration (TTS) allowance meter + pre-load toggle */}
      <div data-testid="growth-tts" className="bg-[#0E131F] rounded-2xl border border-[#1E273D] p-5">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-xs font-semibold uppercase tracking-widest text-[#00E5FF] flex items-center gap-2">
            <Headphones className="w-4 h-4" strokeWidth={1.5} /> Audio narration (Listen) — Amazon Polly
          </h3>
          {tts && (
            <button
              data-testid="tts-prewarm-toggle"
              onClick={toggleTts}
              disabled={savingTts}
              className="flex items-center gap-2 text-xs font-medium text-slate-400 disabled:opacity-50"
              title="When on, a study's audio is pre-loaded when it opens so Listen is instant."
            >
              <span>Pre-load audio</span>
              <span className={`relative inline-flex h-5 w-9 items-center rounded-full transition-colors ${tts.prewarm_enabled ? "bg-[#00E5FF]" : "bg-[#3A4763]"}`}>
                <span className={`inline-block h-4 w-4 transform rounded-full bg-[#0E131F] transition-transform ${tts.prewarm_enabled ? "translate-x-4" : "translate-x-0.5"}`} />
              </span>
              <span className={tts.prewarm_enabled ? "text-[#00E5FF]" : "text-slate-500"}>{tts.prewarm_enabled ? "On" : "Off"}</span>
            </button>
          )}
        </div>
        {tts ? (
          <>
            <div className="grid grid-cols-2 gap-3 mb-3">
              <AllowanceBar label="Standard chars used" used={tts.standard?.used} limit={tts.standard?.limit} />
              <AllowanceBar label="Neural chars used" used={tts.neural?.used} limit={tts.neural?.limit} />
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
              <Metric testid="tts-requests" icon={Headphones} label="Requests this month" value={(tts.requests || 0).toLocaleString()} sub={`${tts.month || ""}`} />
              <Metric testid="tts-syntheses" icon={Activity} label="Fresh syntheses" value={(tts.syntheses || 0).toLocaleString()} sub="not cache hits" />
              <Metric testid="tts-cache-hits" icon={RefreshCw} label="Cache hits" value={(tts.cache_hits || 0).toLocaleString()} sub="reused audio" tone="green" />
            </div>
            {tts.top_users?.length > 0 && (
              <div className="mt-4">
                <div className="text-xs uppercase tracking-widest text-slate-500 mb-2">Top listeners this month</div>
                <div className="space-y-1">
                  {tts.top_users.map((u, i) => (
                    <div key={i} data-testid={`tts-top-user-${i}`} className="flex items-center justify-between text-sm text-slate-400">
                      <span className="truncate">{u.label}</span>
                      <span className="text-slate-500">{u.chars.toLocaleString()} chars</span>
                    </div>
                  ))}
                </div>
              </div>
            )}
            <p className="text-xs text-slate-500 mt-3">
              Every user hears these 6 Polly voices for free within AWS's monthly allowance (5M Standard / 1M
              Neural chars, first 12 months). Once exhausted, listening falls back to the on-device voice
              automatically — no broken experience, no user-facing indicator.
            </p>
          </>
        ) : (
          <p className="text-sm text-slate-500">No audio narration usage recorded yet.</p>
        )}
      </div>

      {/* Premium voice — Pro-tier perk, with its own safety budget + kill-switch */}
      {tts?.premium_voice && (
        <div data-testid="growth-premium-voice" className="bg-[#0E131F] rounded-2xl border border-[#1E273D] p-5">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-xs font-semibold uppercase tracking-widest text-[#00E5FF] flex items-center gap-2">
              <Sparkles className="w-4 h-4" strokeWidth={1.5} /> Premium voice — Pro perk
            </h3>
            <button
              data-testid="premium-voice-toggle"
              onClick={togglePremium}
              disabled={savingPremium}
              className="flex items-center gap-2 text-xs font-medium text-slate-400 disabled:opacity-50"
              title="Instantly revert every Pro listener to the standard voice."
            >
              <span>Enabled</span>
              <span className={`relative inline-flex h-5 w-9 items-center rounded-full transition-colors ${tts.premium_voice.enabled ? "bg-[#00E5FF]" : "bg-[#3A4763]"}`}>
                <span className={`inline-block h-4 w-4 transform rounded-full bg-[#0E131F] transition-transform ${tts.premium_voice.enabled ? "translate-x-4" : "translate-x-0.5"}`} />
              </span>
              <span className={tts.premium_voice.enabled ? "text-[#00E5FF]" : "text-slate-500"}>{tts.premium_voice.enabled ? "On" : "Off"}</span>
            </button>
          </div>
          <div className="bg-[#0B0F16] rounded-xl p-3">
            <div className="flex items-center justify-between text-xs text-slate-400 mb-1.5">
              <span>Monthly safety budget used</span>
              <span data-testid="premium-voice-spend">${(tts.premium_voice.spend_usd || 0).toFixed(2)} / ${tts.premium_voice.budget_usd}</span>
            </div>
            <div className="h-1.5 w-full rounded-full bg-[#1E273D] overflow-hidden">
              {(() => {
                const pct = Math.min(100, Math.round(((tts.premium_voice.spend_usd || 0) / (tts.premium_voice.budget_usd || 1)) * 100));
                const tone = pct >= 90 ? "bg-rose-500" : pct >= 70 ? "bg-amber-500" : "bg-[#00E5FF]";
                return <div className={`h-full ${tone} transition-[width] duration-500`} style={{ width: `${pct}%` }} />;
              })()}
            </div>
          </div>
          {tts.premium_voice.by_user?.length > 0 && (
            <div className="mt-4">
              <div className="text-xs uppercase tracking-widest text-slate-500 mb-2">Spend by Pro user this month</div>
              <div className="space-y-1">
                {tts.premium_voice.by_user.map((u, i) => (
                  <div key={i} data-testid={`premium-voice-user-${i}`} className="flex items-center justify-between text-sm text-slate-400">
                    <span className="truncate">{u.label} <span className="text-slate-600">· {u.tier}</span></span>
                    <span className="text-slate-500">${u.cost_usd.toFixed(2)} ({u.chars.toLocaleString()} chars)</span>
                  </div>
                ))}
              </div>
            </div>
          )}
          <p className="text-xs text-slate-500 mt-3">
            Once the budget is used up, Pro listeners fall back to the standard voice automatically — nothing
            breaks. Turn "Enabled" off any time to revert every Pro user instantly.
          </p>
        </div>
      )}
    </div>
  );
}

function AllowanceBar({ label, used = 0, limit = 1 }) {
  const pct = Math.min(100, Math.round(((used || 0) / (limit || 1)) * 100));
  const tone = pct >= 90 ? "bg-rose-500/100" : pct >= 70 ? "bg-amber-500/100" : "bg-[#00E5FF]";
  return (
    <div className="bg-[#0B0F16] rounded-xl p-3">
      <div className="flex items-center justify-between text-xs text-slate-400 mb-1.5">
        <span>{label}</span>
        <span>{(used || 0).toLocaleString()} / {(limit || 0).toLocaleString()}</span>
      </div>
      <div className="h-1.5 w-full rounded-full bg-[#1E273D] overflow-hidden">
        <div className={`h-full ${tone} transition-[width] duration-500`} style={{ width: `${pct}%` }} />
      </div>
    </div>
  );
}
